import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, MapPin, Instagram } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES } from "../data/site";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#0B1E3F] text-white relative overflow-hidden">
      <div className="grain" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-10 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-6 pb-16 border-b border-white/10">
          <div className="md:col-span-5">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#1D4ED8] mb-4">
              / Contattaci
            </div>
            <h3 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl leading-[0.95] mb-6">
              Hai un progetto?<br />
              <span className="text-[#60A5FA]">Parliamone.</span>
            </h3>
            <Link
              to="/contatti"
              data-testid="footer-cta-contatti"
              className="inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#1E3A8A] text-white px-6 py-4 rounded-full font-display font-bold text-sm tracking-wider uppercase transition-colors"
            >
              Richiedi un sopralluogo
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-500 mb-4">
              / Navigazione
            </div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="font-display font-semibold text-lg hover:text-[#60A5FA] transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-500 mb-4">
              / Servizi
            </div>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/servizi/${s.slug}`}
                    className="font-display font-semibold text-lg hover:text-[#60A5FA] transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2 space-y-4 text-neutral-400 text-sm">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-500 mb-4">
              / Sede
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#1D4ED8]" />
              <span>{COMPANY.address}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 flex-shrink-0 text-[#60A5FA]" />
              <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-white">
                {COMPANY.phone}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 flex-shrink-0 text-[#1D4ED8]" />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-white break-all">
                {COMPANY.email}
              </a>
            </div>
          </div>
        </div>

        <div className="pt-10 border-t border-white/10 -mt-px">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 pb-8">
            <div className="md:col-span-7 font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-500 leading-relaxed">
              <div className="text-white/80 mb-2 font-bold">{COMPANY.legalName}</div>
              <div>Sede legale: {COMPANY.legalAddress}</div>
              <div>P. IVA / C.F. {COMPANY.vat} · REA {COMPANY.rea}</div>
              <div>Capitale Sociale {COMPANY.capital}</div>
              <div className="mt-1">
                PEC: <a href={`mailto:${COMPANY.pec}`} className="hover:text-white break-all">{COMPANY.pec}</a>
              </div>
            </div>
            <div className="md:col-span-5 flex flex-col gap-4 md:items-end">
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="inline-flex items-center gap-2 text-neutral-400 hover:text-white font-mono text-xs tracking-[0.15em] uppercase transition-colors"
              >
                <Instagram className="w-4 h-4" />
                {COMPANY.instagramHandle}
              </a>
              <div className="flex gap-6 font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-500">
                <a href="#" className="hover:text-white">Privacy</a>
                <a href="#" className="hover:text-white">Cookie</a>
                <Link to="/admin" data-testid="footer-admin-link" className="hover:text-white">
                  Area riservata
                </Link>
              </div>
            </div>
          </div>
          <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-600 pt-4 border-t border-white/5">
            © {new Date().getFullYear()} {COMPANY.legalName} · Tutti i diritti riservati
          </div>
        </div>

        {/* Credit banner */}
        <div className="mt-8 pt-6 border-t border-white/5 flex justify-center">
          <a
            href="https://www.studiobiellaweb.it"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-credit"
            className="group inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all"
          >
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500 group-hover:text-neutral-300">
              Sito creato da
            </span>
            <span className="font-display font-bold text-sm tracking-tight text-white group-hover:text-[#60A5FA] transition-colors">
              studiobiellaweb.it
            </span>
            <ArrowUpRight className="w-3.5 h-3.5 text-neutral-400 group-hover:text-[#60A5FA] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
          </a>
        </div>

        <div
          aria-hidden
          className="mt-14 font-display font-black uppercase tracking-tighter leading-none text-white/5 select-none overflow-hidden whitespace-nowrap"
          style={{ fontSize: "clamp(4rem, 20vw, 18rem)" }}
        >
          D'Ambrosio
        </div>
      </div>
    </footer>
  );
}
